import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  BugReport as BugReportIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  VpnKey as ApiKeyIcon,
  Description as DocsIcon,
  Business as OrganizationIcon,
  Groups as TeamIcon,
  People as UsersIcon,
  // Approval as ApprovalIcon,
  Scanner as ScannerIcon,
  Chat as ChatIcon,
  // Bolt as BoltIcon,
  Shield as ShieldIcon,
  Inventory as InventoryIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  WorkspacePremium as SuperAdminIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import NeuraStrikeLogo from '../NeuraStrikeLogo';

const drawerWidth = 240;

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  minRole?: string;
}

const navItems: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Demo Chatbot', icon: <ChatIcon />, path: '/demo-chatbot' },
  { text: 'PII Protection Demos', icon: <ShieldIcon />, path: '/pii-demos' },
  { text: 'Prompt Tester', icon: <BugReportIcon />, path: '/prompt-tester' },
  { text: 'Prompt History', icon: <HistoryIcon />, path: '/prompt-history' },
  { text: 'Security Rules', icon: <SecurityIcon />, path: '/security-rules' },
  { text: 'Model Scanner', icon: <ScannerIcon />, path: '/model-scanner' },
  { text: 'AI Inventory', icon: <InventoryIcon />, path: '/ai-inventory' },
  {
    text: 'Compliance Dashboard',
    icon: <SecurityIcon />,
    path: '/compliance-dashboard',
    minRole: 'team_lead'
  },
  {
    text: 'Incident Management',
    icon: <SecurityIcon />,
    path: '/incidents/dashboard',
    minRole: 'team_lead'
  },
  { text: 'Audit Logs', icon: <SecurityIcon />, path: '/audit-logs', adminOnly: true },
  { text: 'Compliance Testing', icon: <BugReportIcon />, path: '/compliance-testing' },
  { text: 'API Keys', icon: <ApiKeyIcon />, path: '/api-keys' },
  { text: 'API Docs', icon: <DocsIcon />, path: '/api-docs' },
  { text: 'Organization', icon: <OrganizationIcon />, path: '/organization', adminOnly: true },
  { text: 'Team Management', icon: <TeamIcon />, path: '/teams', adminOnly: true },
  { text: 'User Management', icon: <UsersIcon />, path: '/users', adminOnly: true },
  // { text: 'Approval Workflows', icon: <ApprovalIcon />, path: '/approvals', adminOnly: true },
  { text: 'Admin Panel', icon: <AdminIcon />, path: '/admin', adminOnly: true },
  { text: 'Super Admin', icon: <SuperAdminIcon />, path: '/super-admin', superAdminOnly: true }
];

const Layout: React.FC = () => {
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isDark = mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleProfileMenuClose();
  };

  // Role hierarchy for filtering
  const roleHierarchy: { [key: string]: number } = {
    user: 1,
    team_member: 2,
    team_lead: 3,
    department_head: 4,
    moderator: 5,
    admin: 6,
    super_admin: 7
  };

  const hasMinimumRole = (minRole?: string): boolean => {
    if (!minRole) return true;
    if (!user?.role) return false;

    const userRoleLevel = roleHierarchy[user.role] || 0;
    const minRoleLevel = roleHierarchy[minRole] || 0;

    return userRoleLevel >= minRoleLevel;
  };

  const filteredNavItems = navItems.filter((item) => {
    // Super admin only items
    if (item.superAdminOnly && user?.role !== 'super_admin') {
      return false;
    }

    // Check admin-only items
    if (item.adminOnly && user?.role !== 'admin' && user?.role !== 'super_admin') {
      return false;
    }

    // Check minimum role requirement
    if (item.minRole && !hasMinimumRole(item.minRole)) {
      return false;
    }

    return true;
  });

  const drawer = (
    <div>
      <Toolbar sx={{ py: 2, px: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            justifyContent: 'center'
          }}
        >
          <NeuraStrikeLogo height={32} />
        </Box>
      </Toolbar>
      <Divider sx={{ mx: 2, borderColor: isDark ? '#1e293b' : '#e2e8f0' }} />
      <List sx={{ px: 1, py: 2 }}>
        {filteredNavItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                minHeight: 44,
                borderRadius: 2,
                '&.Mui-selected': {
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(236, 72, 153, 0.1) 100%)'
                    : 'linear-gradient(135deg, #eef2ff 0%, #fce7f3 100%)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: location.pathname === item.path ? 600 : 500
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backdropFilter: 'blur(8px)',
          background: isDark ? '#070f1c' : 'rgba(255, 255, 255, 0.95)',
          borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: 'none' },
              color: '#6366f1'
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                color: isDark ? '#f1f5f9' : '#1e293b',
                fontSize: '1.25rem',
                letterSpacing: '-0.025em'
              }}
            >
              {navItems.find((item) => item.path === location.pathname)?.text || 'NeuraStrike'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                fontSize: '0.75rem'
              }}
            >
              Real-time AI Security Protection
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Theme Toggle Button */}
            <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              <IconButton
                onClick={toggleTheme}
                sx={{
                  color: isDark ? '#fbbf24' : '#6366f1',
                  bgcolor: isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(99, 102, 241, 0.2)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {isDark ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              <Typography
                variant="body2"
                sx={{ color: isDark ? '#f1f5f9' : '#1e293b', fontWeight: 600 }}
              >
                {user?.email}
              </Typography>
              {user?.organization?.name && (
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? '#94a3b8' : '#64748b',
                    fontSize: '0.688rem',
                    display: 'block',
                    mb: 0.25
                  }}
                >
                  {user.organization.name}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  color: '#6366f1',
                  textTransform: 'uppercase',
                  fontSize: '0.688rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em'
                }}
              >
                {user?.role}
              </Typography>
            </Box>
            <Button
              onClick={handleProfileMenuOpen}
              sx={{
                minWidth: 'auto',
                p: 0.5,
                borderRadius: 2,
                '&:hover': {
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9'
                }
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                }}
              >
                <PersonIcon />
              </Avatar>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: isDark ? '#070f1c' : '#ffffff'
            }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: isDark ? '#070f1c' : '#ffffff',
              borderRight: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '80px', // Height of AppBar
          minHeight: 'calc(100vh - 80px)'
        }}
      >
        <Box className="fade-in">
          <Outlet />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
      >
        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
